/**
 * Kai Voice captions follow the voice. The server pushes Kai's answer one
 * sentence at a time, a moment before GPT-Live says it; GPT-Live's output
 * transcript (fragments of 200 ms of speech) is followed word by word
 * through that text, and the store reveals each word when the voice says
 * it. The overlay lays out the sentence being spoken in full and fades its
 * words in, so nothing reflows while Kai talks — never the whole answer at
 * once.
 *
 * Sentence boundaries are where the pushes ended (`chunkEnds`): the server's
 * chunker already knows "am 3. Oktober" and "z. B." are no sentence ends.
 */

export interface CaptionToken {
  text: string;
  /** The whitespace after the word, so the sentence renders exactly as written. */
  gap: string;
  start: number;
}

// Chinese and Japanese put no spaces between words: every character is a word.
const CJK = "\\u3040-\\u30ff\\u3400-\\u4dbf\\u4e00-\\u9fff\\uf900-\\ufaff";
const CJK_CHAR = new RegExp(`^[${CJK}]$`);
const TOKEN = new RegExp(`[${CJK}]|[^\\s${CJK}]+`, "g");
const SPOKEN = /[\p{L}\p{N}]/u;

/** Words (punctuation stays attached to the word before it). */
export const captionTokens = (text: string): CaptionToken[] => {
  const source = text ?? "";
  const tokens: CaptionToken[] = [];
  TOKEN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN.exec(source)) !== null) {
    const end = match.index + match[0].length;
    const gap = source.slice(end).match(/^\s*/)?.[0] ?? "";
    const previous = tokens[tokens.length - 1];
    if (!SPOKEN.test(match[0]) && previous) {
      previous.text += previous.gap + match[0];
      previous.gap = gap;
    } else {
      tokens.push({ text: match[0], gap, start: match.index });
    }
  }
  return tokens;
};

export const countCaptionWords = (text: string): number => captionTokens(text).length;

const captionSentences = (text: string, chunkEnds: number[]): CaptionToken[][] => {
  const sentences: CaptionToken[][] = [];
  let chunk = 0;
  for (const token of captionTokens(text)) {
    while (chunk < chunkEnds.length - 1 && token.start >= chunkEnds[chunk]) chunk += 1;
    (sentences[chunk] = sentences[chunk] ?? []).push(token);
  }
  return sentences.filter(Boolean);
};

/**
 * The sentence the spoken-word cursor is in (the last one once everything is
 * spoken) with how many of its words were said.
 */
export const captionSentence = (
  text: string,
  spokenWords: number,
  chunkEnds: number[],
): { index: number; tokens: CaptionToken[]; spoken: number } => {
  const sentences = captionSentences(text, chunkEnds);
  let before = 0;
  for (let index = 0; index < sentences.length; index++) {
    const tokens = sentences[index];
    if (spokenWords <= before + tokens.length || index === sentences.length - 1) {
      return { index, tokens, spoken: Math.min(Math.max(spokenWords - before, 0), tokens.length) };
    }
    before += tokens.length;
  }
  return { index: 0, tokens: [], spoken: 0 };
};

export interface CaptionAlignment {
  /** Caption words GPT-Live has said. */
  heard: number;
  /** Transcript words already followed through the caption. */
  consumed: number;
  /** Transcript words since the last one found in the caption: GPT-Live is rewording. */
  unmatched: number;
  /** The caption arrived while GPT-Live was still talking: it starts with its own opening words. */
  awaitingOpening: boolean;
}

// A heard word may pass this many caption words GPT-Live left out, plus one
// per reworded word before it.
const SKIP_REACH = 3;
// "der", "und", "is": too common to be looked for further ahead.
const SHORT_WORD_LENGTH = 3;
const OPENING_WORDS = 2;
// A reworded opening never matches: after this many words the caption follows anyway.
const OPENING_GIVE_UP_WORDS = 6;

export const startCaptionAlignment = (awaitingOpening: boolean): CaptionAlignment => ({
  heard: 0,
  consumed: 0,
  unmatched: 0,
  awaitingOpening,
});

/** "Analysten" for "Analyst", "E-Mail" for "E-Mail-Adresse": longer words match by their stem. */
const sameWord = (spoken: string, written: string): boolean =>
  spoken === written ||
  (spoken.length > SHORT_WORD_LENGTH &&
    written.length > SHORT_WORD_LENGTH &&
    (written.startsWith(spoken) || spoken.startsWith(written)));

/**
 * Follows GPT-Live's transcript through the caption from `from` on. Words
 * GPT-Live adds or rewords count as `unmatched`; words it leaves out are
 * passed once a later word is heard. Deterministic, so every fragment can
 * re-run it on the whole transcript since `from`.
 */
export const alignCaptionSpeech = (caption: string, transcript: string, from: CaptionAlignment): CaptionAlignment => {
  const written = captionTokens(caption).map((token) => normalizeCaptionWord(token.text));
  const spoken = captionTokens(transcript).map((token) => normalizeCaptionWord(token.text));
  // The last word may still grow ("Kle" → "Kleinschreibung").
  const complete = /\s$/.test(transcript ?? "") ? spoken.length : spoken.length - 1;
  let { heard, consumed, unmatched, awaitingOpening } = from;

  if (awaitingOpening) {
    // A distinctive first word is enough; "Ich …" or "Ja, …" could still be GPT-Live's filler.
    const opening = (written[0] ?? "").length > SHORT_WORD_LENGTH ? written.slice(0, 1) : written.slice(0, OPENING_WORDS);
    if (opening.length === 0) return from;
    for (let at = consumed; at + opening.length <= spoken.length; at++) {
      if (opening.every((word, offset) => sameWord(spoken[at + offset], word))) {
        return alignCaptionSpeech(caption, transcript, {
          heard: opening.length,
          consumed: at + opening.length,
          unmatched: 0,
          awaitingOpening: false,
        });
      }
    }
    if (spoken.length - consumed <= OPENING_GIVE_UP_WORDS) return from;
    // Reworded, dropped or said before the caption arrived: follow everything since `from`.
    awaitingOpening = false;
  }

  for (; consumed < spoken.length; consumed++) {
    const word = spoken[consumed];
    // After a word GPT-Live said differently, a short one may pass that one caption word.
    // The reach grows faster than the transcript, so a dropped passage is caught up
    // with; a Chinese or Japanese character is a word of its own.
    const long = word.length > SHORT_WORD_LENGTH || CJK_CHAR.test(word);
    const skip = long ? SKIP_REACH + 2 * unmatched : Math.min(unmatched, 1);
    const reach = Math.min(written.length, heard + 1 + skip);
    let found = -1;
    for (let index = heard; index < reach; index++) {
      if (sameWord(word, written[index])) {
        found = index;
        break;
      }
    }
    // A growing word only counts once it already is the next caption word ("Kle…" for "Kleinschreibung");
    // until then it is being said, so the display moves on by it.
    const growing = consumed >= complete;
    if (growing && found !== heard && !(word.length > 1 && (written[heard] ?? "").startsWith(word))) {
      return { heard, consumed, unmatched: word ? unmatched + 1 : unmatched, awaitingOpening };
    }
    if (growing && found !== heard) found = heard;
    if (found >= 0) {
      heard = found + 1;
      unmatched = 0;
    } else if (word) {
      unmatched += 1;
    }
  }
  return { heard, consumed, unmatched, awaitingOpening };
};

/**
 * Caption words said so far, never into a sentence whose first word has not
 * been heard. A reworded passage moves on word by word as GPT-Live speaks,
 * up to its sentence's end.
 */
export const captionWordsShown = (caption: string, chunkEnds: number[], alignment: CaptionAlignment): number => {
  let end = 0;
  for (const tokens of captionSentences(caption, chunkEnds)) {
    const start = end;
    end += tokens.length;
    if (alignment.heard < end) {
      if (alignment.heard === start) return alignment.heard;
      return Math.min(end, alignment.heard + alignment.unmatched);
    }
  }
  return alignment.heard;
};

/**
 * Kai stopped talking: the sentence it was in was said. With `maxMissing`
 * (Kai's voice paused) only a sentence missing that few words is finished,
 * so a comma or a long number does not end it early. Without it (a long
 * silence), a first sentence nothing of which was matched counts too:
 * GPT-Live reworded its opening.
 */
export const completeCaptionSentence = (
  caption: string,
  chunkEnds: number[],
  transcript: string,
  alignment: CaptionAlignment,
  maxMissing?: number,
): CaptionAlignment => {
  const spoken = captionTokens(transcript);
  const spokenWords = spoken.length;
  // A word still arriving is counted as unmatched but not consumed yet.
  const arriving = alignment.consumed < spokenWords && normalizeCaptionWord(spoken[alignment.consumed].text) ? 1 : 0;
  let end = 0;
  for (const tokens of captionSentences(caption, chunkEnds)) {
    const start = end;
    end += tokens.length;
    if (alignment.heard >= end) continue;
    // While the opening is awaited, what was said is GPT-Live's own filler.
    const begun =
      alignment.heard > start || (maxMissing === undefined && start === 0 && spokenWords > 0 && !alignment.awaitingOpening);
    if (!begun || end - alignment.heard > (maxMissing ?? Number.POSITIVE_INFINITY)) return alignment;
    // The words after the last match may already be the next sentence's.
    const rewind = Math.max(alignment.unmatched - arriving, 0);
    return { heard: end, consumed: Math.max(alignment.consumed - rewind, 0), unmatched: 0, awaitingOpening: false };
  }
  return alignment;
};

/** The pushed sentences as text, for the screen-reader live region. */
export const captionChunks = (text: string, chunkEnds: number[]): string[] =>
  chunkEnds.map((end, index) => text.slice(index === 0 ? 0 : chunkEnds[index - 1], end).trim()).filter(Boolean);

/** "Der", "der," and "DER." are the same spoken word. */
export const normalizeCaptionWord = (word: string): string =>
  (word ?? "").toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");

// Kai's voice reads about this fast (letters per second) until its pauses tell the
// call's real pace (14 to 22 measured on GPT-Live).
const READING_RATE = 17 / 1000;
const MIN_READING_RATE = 12 / 1000;
const MAX_READING_RATE = 26 / 1000;
// A phrase shorter than this says too little about the pace.
const CALIBRATION_MIN_MS = 600;
const CALIBRATION_MIN_WEIGHT = 8;
// While Kai speaks, a word shows this far (in reading weight, about a quarter
// second) before the cursor reaches it: it fades in over a third of a second.
const SPEAKING_LEAD = 4;
// A digit is said as several letters ("179" → "hundertneunundsiebzig"), a
// Chinese or Japanese character as a syllable.
const DIGIT_WEIGHT = 3;
const CJK_WEIGHT = 3;
// The transcript leads the voice by up to about 800 ms, so a cursor more than about a
// second and a half behind it is behind the voice too (fast speech, a stall) and speeds
// up; close to it, the cursor is ahead of the voice and slows down. Tuned on recorded calls.
const CATCH_UP_BEHIND = 24;
const EASE_AHEAD = 4;
const CATCH_UP_SPEED = 1.4;
const EASE_SPEED = 0.5;
// Gaps between syllables are shorter than this.
const VOICE_HANGOVER_MS = 150;
const VOICE_LEVEL = 0.015;
// Without a meter that hears the voice, speech is where transcript arrives.
const TRANSCRIPT_SPEECH_MS = 500;
// Transcript for this long but never a sound on the meter: it cannot hear the voice
// (a suspended audio context in some browsers).
const DEAF_METER_MS = 1500;
// Kai pauses at punctuation: at a pause, the phrase the cursor is in was said to its
// end when the transcript already passed it.
const PAUSE_FINISHES_WORDS = 6;

const wordWeight = (text: string): number => {
  let weight = 1;
  for (const char of normalizeCaptionWord(text)) {
    weight += /\p{N}/u.test(char) ? DIGIT_WEIGHT : CJK_CHAR.test(char) ? CJK_WEIGHT : 1;
  }
  return weight;
};

/**
 * Reveals the caption at the pace Kai reads it. GPT-Live's transcript says
 * which words are said but not when (it runs 150 to 800 ms ahead, by a
 * varying amount, and its `start_ms` is no audio position): the cursor moves
 * at speaking pace while Kai's voice is audible, stops when it pauses, and
 * never passes what the transcript reached. `now` is passed in, so recorded
 * calls replay exactly.
 */
export class CaptionReadingCursor {
  private cursor = 0;
  private starts: number[] = [];
  private ends: number[] = [];
  private phraseEnds: number[] = [];
  private sentenceEnds: number[] = [];
  private loudAt = Number.NEGATIVE_INFINITY;
  private lastTick: number;
  private lastTranscriptAt = Number.NEGATIVE_INFINITY;
  private firstTranscriptAt: number | null = null;
  private heardVoice = false;
  private deaf = false;
  private shown = 0;
  private rate = READING_RATE;
  private phraseStart = 0;
  private phraseVoiceMs = 0;

  constructor(now: number) {
    this.lastTick = now;
  }

  /** A new caption (or more of it): word positions in reading weight. */
  setCaption(caption: string, chunkEnds: number[]): void {
    const tokens = captionTokens(caption);
    const sentenceEnds = new Set<number>();
    let words = 0;
    for (const sentence of captionSentences(caption, chunkEnds)) sentenceEnds.add((words += sentence.length));
    let position = 0;
    this.starts = [];
    this.ends = [];
    this.phraseEnds = [];
    this.sentenceEnds = Array.from(sentenceEnds);
    tokens.forEach((token, index) => {
      this.starts.push(position);
      position += wordWeight(token.text);
      this.ends.push(position);
      if (sentenceEnds.has(index + 1) || /[,;:.!?…。，、！？；：]["'”“»)」』]*$/.test(token.text)) this.phraseEnds.push(index + 1);
    });
  }

  /** Restart at the top of a new caption; the pace learned so far stays. */
  reset(now: number): void {
    this.cursor = 0;
    this.shown = 0;
    this.phraseStart = 0;
    this.phraseVoiceMs = 0;
    this.lastTick = now;
  }

  transcriptArrived(now: number): void {
    this.lastTranscriptAt = now;
    this.firstTranscriptAt = this.firstTranscriptAt ?? now;
  }

  /**
   * Advance to `now`. `reached`: caption words the transcript reached (the
   * cursor never passes them); `level`: Kai's voice level (null: no meter).
   * Returns the caption words to show.
   */
  tick(now: number, reached: number, level: number | null): number {
    const dt = Math.max(0, now - this.lastTick);
    this.lastTick = now;
    const meter = this.meterReading(now, level);
    if (meter !== null && meter > VOICE_LEVEL) {
      this.heardVoice = true;
      this.loudAt = now;
    }
    const speaking =
      meter === null ? now - this.lastTranscriptAt < TRANSCRIPT_SPEECH_MS : now - this.loudAt < VOICE_HANGOVER_MS;
    const limit = this.limitOf(reached);
    let position = this.cursor;
    if (speaking) {
      const behind = limit - this.cursor;
      const speed = behind > CATCH_UP_BEHIND ? CATCH_UP_SPEED : behind < EASE_AHEAD ? EASE_SPEED : 1;
      this.phraseVoiceMs += dt;
      this.cursor = Math.min(limit, this.cursor + this.rate * speed * dt);
      position = this.cursor > 0 ? Math.min(limit, this.cursor + SPEAKING_LEAD) : 0;
    }
    return this.show(position);
  }

  /** How long Kai's voice has been silent, or null where the meter cannot tell. */
  silentFor(now: number): number | null {
    return this.deaf || !this.heardVoice ? null : now - this.loudAt;
  }

  /**
   * Kai paused: the phrase the cursor is in was said to its end, and how long
   * that took is the call's reading pace.
   */
  finishPhrase(reached: number): number {
    const phraseEnd = this.phraseEnds.find((end) => end > this.shown);
    const atPhraseEnd = this.phraseEnds.includes(this.shown);
    if (phraseEnd !== undefined && phraseEnd <= reached && phraseEnd - this.shown <= PAUSE_FINISHES_WORDS) {
      this.cursor = Math.max(this.cursor, this.ends[phraseEnd - 1]);
    } else if (!atPhraseEnd) {
      // A pause inside a phrase tells nothing about the pace.
      this.phraseStart = this.cursor;
      this.phraseVoiceMs = 0;
      return this.show(this.cursor);
    }
    const weight = this.cursor - this.phraseStart;
    if (this.phraseVoiceMs >= CALIBRATION_MIN_MS && weight >= CALIBRATION_MIN_WEIGHT) {
      const measured = weight / this.phraseVoiceMs;
      this.rate = Math.min(MAX_READING_RATE, Math.max(MIN_READING_RATE, (this.rate + measured) / 2));
    }
    this.phraseStart = this.cursor;
    this.phraseVoiceMs = 0;
    return this.show(this.cursor);
  }

  /** A long silence: the sentence Kai was in was said (never several at once: each gets its turn on screen). */
  finish(reached: number): number {
    const sentenceEnd = this.sentenceEnds.find((end) => end > this.shown) ?? reached;
    this.cursor = Math.max(this.cursor, this.limitOf(Math.min(reached, sentenceEnd)));
    return this.show(this.cursor);
  }

  private meterReading(now: number, level: number | null): number | null {
    if (!this.heardVoice && this.firstTranscriptAt !== null && now - this.firstTranscriptAt >= DEAF_METER_MS) this.deaf = true;
    return this.deaf ? null : level;
  }

  private limitOf(reached: number): number {
    return reached > 0 ? this.ends[Math.min(reached, this.ends.length) - 1] : 0;
  }

  private show(position: number): number {
    this.shown = Math.max(this.shown, this.wordsAt(position));
    return this.shown;
  }

  /** Words whose start the cursor passed: a word shows as Kai starts it. */
  private wordsAt(position: number): number {
    let words = 0;
    while (words < this.starts.length && this.starts[words] < position) words++;
    return words;
  }
}
